import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

const INDUSTRY_ICONS = {
 hospitality: '️', construction: '️', retail: '️', events: '',
 warehouse: '', cleaning: '', security: '️', admin: '', general: '⚡',
};

export default function CityPage() {
 const { slug } = useParams();
 const navigate = useNavigate();
 const [city, setCity] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const load = async () => {
 try {
 const res = await axios.get(`/api/cities/${slug}`);
 setCity(res.data);
 } catch { navigate('/404'); }
 setLoading(false);
 };
 load();
 }, [slug]);

 if (loading) return <div className="min-h-screen bg-neutral-50 flex items-center justify-center"><div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
 if (!city) return null;

 const avgPay = parseFloat(city.stats?.avg_pay || 0);
 const totalShifts = parseInt(city.stats?.total || 0);

 return (
 <div className="min-h-screen bg-neutral-50">
 <SEOHelmet title={city.meta_title} description={city.meta_description} />

 {/* Nav */}
 <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
 <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <Link to="/" className="flex items-center gap-2">
 <img src="/logo.svg" alt="ShiftMatch" className="h-7" onError={e => { e.target.style.display='none'; }} />
 <span className="font-bold text-neutral-900 text-sm">ShiftMatch</span>
 </Link>
 <span className="text-neutral-300">/</span>
 <span className="text-sm text-neutral-600">{city.city}</span>
 </div>
 <div className="flex gap-2">
 <Link to="/login" className="text-xs px-3 py-1.5 border border-neutral-200 rounded-lg text-neutral-600 hover:bg-neutral-50">Sign In</Link>
 <Link to="/register" className="text-xs px-3 py-1.5 bg-brand-500 text-white rounded-lg font-bold hover:bg-brand-600">Sign Up Free</Link>
 </div>
 </div>
 </div>

 {/* Hero */}
 <div className="bg-gradient-to-r from-brand-600 to-brand-500 text-white">
 <div className="max-w-4xl mx-auto px-4 py-10">
 <h1 className="text-3xl font-black mb-2">{city.headline}</h1>
 <p className="text-brand-100 text-sm max-w-xl leading-relaxed">{city.description}</p>
 <div className="flex gap-4 mt-5">
 <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2.5">
 <p className="text-2xl font-black">{totalShifts}</p>
 <p className="text-[10px] text-brand-100">shifts available</p>
 </div>
 {avgPay > 0 && (
 <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2.5">
 <p className="text-2xl font-black">${avgPay.toFixed(0)}<span className="text-sm font-normal">/hr</span></p>
 <p className="text-[10px] text-brand-100">average pay</p>
 </div>
 )}
 <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2.5">
 <p className="text-2xl font-black">$0</p>
 <p className="text-[10px] text-brand-100">cost to apply</p>
 </div>
 </div>
 <Link to="/register" className="inline-block mt-6 px-6 py-3 bg-white text-brand-700 font-bold rounded-xl hover:bg-brand-50 transition shadow-sm">
 Start Earning in {city.city} →
 </Link>
 </div>
 </div>

 {/* How it works */}
 <div className="max-w-4xl mx-auto px-4 py-8">
 <h2 className="text-xl font-bold text-neutral-900 mb-5 text-center">How It Works</h2>
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
 {[
 { step: '1', icon: '', title: 'Sign up free', desc: 'Create your account in 30 seconds. No CV needed.' },
 { step: '2', icon: '', title: 'Browse & apply', desc: 'Find shifts that suit you. One-tap apply.' },
 { step: '3', icon: '', title: 'Work & get paid', desc: 'Show up, check in, get paid within 24 hours.' },
 ].map(s => (
 <div key={s.step} className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm text-center">
 <div className="text-3xl mb-2">{s.icon}</div>
 <h3 className="font-bold text-neutral-900 text-sm mb-1">{s.title}</h3>
 <p className="text-xs text-neutral-500">{s.desc}</p>
 </div>
 ))}
 </div>

 {/* Available shifts */}
 <h2 className="text-xl font-bold text-neutral-900 mb-4">Available Shifts in {city.city}</h2>
 {city.shifts?.length === 0 ? (
 <div className="text-center py-10 bg-white rounded-2xl border border-neutral-100">
 <p className="text-neutral-400 text-sm">No shifts in {city.city} right now.</p>
 <p className="text-xs text-neutral-300 mt-1">New shifts post daily — <Link to="/register" className="text-brand-600 underline">sign up</Link> to get notified.</p>
 </div>
 ) : (
 <div className="space-y-2.5 mb-8">
 {city.shifts?.slice(0, 10).map(s => {
 const industry = (s.industry || 'general').toLowerCase();
 const icon = INDUSTRY_ICONS[industry] || '⚡';
 return (
 <div key={s.id} className="bg-white rounded-2xl p-4 border border-neutral-100 shadow-sm hover:shadow-md transition">
 <div className="flex items-start justify-between gap-3">
 <div className="flex items-start gap-3 flex-1">
 <div className="w-9 h-9 rounded-xl bg-neutral-50 flex items-center justify-center text-base shrink-0">{icon}</div>
 <div className="flex-1 min-w-0">
 <h3 className="font-bold text-neutral-900 text-sm">{s.title}</h3>
 <div className="flex flex-wrap gap-2 text-xs text-neutral-500 mt-0.5">
 <span> {s.location}</span>
 <span>⏱ {s.hours}h</span>
 {s.entry_level && <span className="text-brand-600 font-medium">✓ No exp needed</span>}
 </div>
 </div>
 </div>
 <div className="text-right shrink-0">
 <p className="font-black text-neutral-900">${s.hourly_rate}<span className="text-xs font-normal text-neutral-400">/hr</span></p>
 <Link to="/register" className="inline-block mt-1.5 px-3 py-1 bg-brand-500 text-white text-xs font-bold rounded-lg hover:bg-brand-600">Apply</Link>
 </div>
 </div>
 </div>
 );
 })}
 {city.shifts?.length > 10 && (
 <div className="text-center py-3">
 <Link to="/register" className="text-sm text-brand-600 font-bold hover:underline">See all {city.shifts.length} shifts →</Link>
 </div>
 )}
 </div>
 )}

 {/* For employers */}
 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 mb-8">
 <h2 className="text-lg font-bold text-neutral-900 mb-2"> Hiring in {city.city}?</h2>
 <p className="text-sm text-neutral-500 mb-4">Post shifts for free. Get applicants in minutes. Only pay 15% when someone actually works the shift.</p>
 <Link to="/register" className="inline-block px-5 py-2.5 bg-brand-500 text-white font-bold rounded-xl text-sm hover:bg-brand-600">Post a Shift Free →</Link>
 </div>

 {/* Other cities */}
 <div className="text-center">
 <h3 className="font-bold text-neutral-900 text-sm mb-3">Also available in</h3>
 <div className="flex flex-wrap justify-center gap-2">
 {['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide'].filter(s => s !== slug).map(s => (
 <Link key={s} to={`/shifts/${s}`} className="text-xs px-3 py-1.5 bg-white border border-neutral-200 rounded-lg text-neutral-600 hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200 transition capitalize">
 {s}
 </Link>
 ))}
 </div>
 </div>
 </div>

 <footer className="text-center py-5 text-[10px] text-neutral-400 border-t border-neutral-100 mt-6">
 © 2026 ShiftMatch Australia · <a href="/terms.html" className="hover:text-brand-600">Terms</a> · <a href="/privacy.html" className="hover:text-brand-600">Privacy</a>
 </footer>
 </div>
 );
}
